import React, { MouseEvent, useEffect, useState } from 'react';
import styles from '../../../TopNavBar.module.scss';
import { useCurrencyContext } from 'app/contexts/Currency/CurrencyContext';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { CurrencyChangeWarningOverlay } from './CurrencyChangeWarningOverlay';
import { useRootStore } from 'app/contexts/RootStoreContext';
import { ExternalLinks } from 'shared/enums/external-links.enum';
import { Currency } from 'shared/components/CurrencySelect/types';

interface ActiveCurrencyButton {
  toggleOpenList: () => void;
  selectedCurrency: Currency | null;
  resetCurrency: (event: MouseEvent<HTMLSpanElement>) => void;
}

export const ActiveCurrencyButton: React.FC<ActiveCurrencyButton> = ({
  toggleOpenList,
  selectedCurrency,
  resetCurrency,
}: ActiveCurrencyButton): React.ReactNode => {
  const currencyContext = useCurrencyContext();
  const { usersStore } = useRootStore();
  const [showResetButton, setShowResetButton] = useState(!!currencyContext.sessionCurrencyCode);

  const [adminChangedCurrencySymbol, setAdminChangedCurrencySymbol] = useState<string | null>(null);

  const [showWarningOverlay, setShowWarningOverlay] = useState(false);

  const onDismissWarningOverlay = () => {
    usersStore.updateAdminProcessingCurrency(null);
    setShowWarningOverlay(false);
  };

  useEffect(() => {
    if (currencyContext.isProcessingCurrencyChangedByAdmin) {
      const symbol = currencyContext.currenciesMap[currencyContext.isProcessingCurrencyChangedByAdmin]?.symbolNative;
      setAdminChangedCurrencySymbol(symbol);

      setShowWarningOverlay(true);
      const timer = setTimeout(() => {
        onDismissWarningOverlay();
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      onDismissWarningOverlay();
    }
  }, [currencyContext.isProcessingCurrencyChangedByAdmin]);

  useEffect(() => {
    setShowResetButton(!!currencyContext.sessionCurrencyCode);
  }, [currencyContext.sessionCurrencyCode]);

  const localResetCurrency = (event: MouseEvent<HTMLSpanElement>) => {
    resetCurrency(event);
    setShowResetButton(false);
    setShowWarningOverlay(false);
  };

  return (
    <div className={styles.activeButton}>
      <div className={styles.activeButtonContent}>
        <div className={styles.currencyCode}>
          {selectedCurrency?.code} ({selectedCurrency?.symbolNative})
        </div>
        {showResetButton ? (
          <Tooltip
            placement="bottom"
            classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
            title={<span className={styles.textTooltip}>Reset to Processing Currency</span>}
          >
            <div className={styles.resetButton}>
              <GenerateIcon iconName={ICONS.reset.name} onClick={(event) => localResetCurrency(event)} />
            </div>
          </Tooltip>
        ) : null}
        <Tooltip
          placement="left"
          classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
          title={
            <div className={styles.longTextTooltip}>
              <div>Set the displayed currency for this session</div>
              <div>
                <a href={ExternalLinks.CurrenciesDocumentation} target="_blank" className={styles.navigateText}>
                  Learn more
                </a>
              </div>
            </div>
          }
        >
          <div className={styles.chevron} onClick={() => toggleOpenList()}>
            <GenerateIcon iconName={ICONS.chevronDown.name} />
          </div>
        </Tooltip>
      </div>
      {showWarningOverlay ? (
        <div className={styles.warningOverlay}>
          <CurrencyChangeWarningOverlay
            adminChangedCurrencySymbol={adminChangedCurrencySymbol}
            selectedCurrency={selectedCurrency}
            onDismiss={onDismissWarningOverlay}
          />
        </div>
      ) : null}
    </div>
  );
};
